/**
 * An address is one contactable unit — a house, or suite 210 in a tower.
 * Org-scoped record; `registry_id` links to the global address registry once
 * phase 2 lands and stays null until then. Rows are created lazily on first
 * interaction. See backend/schemas/customer.sql and
 * docs/implementation/canvassing-crm-high-level-design.md §5.
 */

export type PlaceType = 'residence' | 'business' | 'multi_unit' | 'unknown';
export const PLACE_TYPES: readonly PlaceType[] = ['residence', 'business', 'multi_unit', 'unknown'];

export type GeocodeStatus = 'resolved' | 'pending' | 'manual' | 'failed';

export interface Address {
  id: string;
  registry_id: string | null;
  formatted_address: string;
  unit: string;
  house_number: string;
  street: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  place_type: PlaceType;
  business_name: string;
  lat: number | null;
  lng: number | null;
  building_id: string;
  registry_snapshot: string;
  registry_synced_at: number | null;
  is_locally_edited: number;
  geocode_status: GeocodeStatus;
  address_verified: number;
  do_not_contact: number;
  do_not_contact_reason: string;
  do_not_contact_at: number | null;
  do_not_contact_by: string;
  last_interaction_at: number | null;
  last_outcome_kind: string;
  last_objection_id: string;
  notes: string;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
  deleted_at: number | null;
}

/** The user-editable subset of an address. */
export interface AddressFields {
  formatted_address: string;
  unit: string;
  house_number: string;
  street: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  place_type: PlaceType;
  business_name: string;
  notes: string;
}

/** Column names of {@link AddressFields}, in one place for diffing and SQL. */
export const ADDRESS_EDITABLE_FIELDS: readonly (keyof AddressFields)[] = [
  'formatted_address',
  'unit',
  'house_number',
  'street',
  'city',
  'region',
  'postal_code',
  'country',
  'place_type',
  'business_name',
  'notes',
];

export interface CreateAddressParams extends Partial<AddressFields> {
  formatted_address: string;
  lat?: number | null;
  lng?: number | null;
  geocode_status?: GeocodeStatus;
  /** Set when created by tapping a registry building (map, phase 3) — links back to the
   *  global address registry. Left unset for a manually-entered or pin-dropped address. */
  registry_id?: string | null;
  /** The registry building's own id, for grouping this org's units under one footprint on
   *  the map — set alongside `registry_id`, or independently for a "add unit" flow. */
  building_id?: string;
}

export interface UpdateAddressParams extends Partial<AddressFields> {
  id: string;
}

/** Outcome of {@link AddressService.delete} -- 'has_interactions' means the
 *  address has history that a delete would orphan, so it was refused. */
export type DeleteAddressResult =
  | { success: true }
  | { success: false; reason: 'not_found' | 'has_interactions' };
